import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { getDisciplineCodeByDescription } from "configs/discipline-user-config";
import { Content, ModalController } from "ionic-angular";
import Company, { CompanyLookup } from "models/company-details/company";
import { PagedCompanyLocation } from "models/company-details/company-location";
import SortOptions from "models/content-wrapper/sort-options";
import Role from "models/system-admin/roles";
import User from "models/system-admin/user";
import { CompanyManagementService } from "providers/company-management-service/company-management-service";
import { RolesService } from "providers/roles/roles.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Subscription } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import RoleUpdate from "../../../models/system-admin/role-update";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { UserProfileService } from "../../../providers/user-profile/user-profile.service";

@Component({
  selector: "add-user-to-role",
  templateUrl: "add-user-to-role.html",
})
export class AddUserToRole implements OnInit, OnDestroy {
  @Input() company: Company;
  @Input() role: Role;
  @Input() isEdit: boolean;

  @Output()
  hideComponent = new EventEmitter<boolean>();

  private contentPlaceholder: Content;
  hasScrollbar: boolean = false;

  @ViewChild("showMore") showMore: ElementRef;
  @ViewChild("contentPlaceholder") set content(content: Content) {
    if (content) {
      this.contentPlaceholder = content;
      this.contentPlaceholder.resize();
      this.checkForScrollbar();
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    this.checkForScrollbar();
  }

  departmentList: CompanyLookup[] = [];
  disciplineList: CompanyLookup[] = [];
  locationList: CompanyLookup[] = [];
  departmentSelected: string;
  disciplineSelected: string;
  locationSelected: string;
  query: string;
  usersData: User[] = [];
  usersSubscription: Subscription;
  public currentPage: number = 0;
  sortOptions: SortOptions = new SortOptions("firstName", "asc");
  protected allUsersSelected: boolean = false;
  filterParameters: any = {};
  pageSize: number = 10;
  totalPageCount: number = 0;
  lastFrom: number = 0;
  lastRecords = {
    currentFrom: null,
    totalHits: 0,
  };
  showFilters: boolean = true;
  loadingUsers: boolean;
  private boundOnFilter: () => void;
  private searchIconEl: Element | null = null;

  constructor(
    private utilityService: UtilityService,
    private modalController: ModalController,
    private elementRef: ElementRef,
    private roleService: RolesService,
    private userProfileService: UserProfileService,
    private companyManagementService: CompanyManagementService,
    private resourceManagementService: ResourceManagementService
  ) {}

  ngOnInit(): void {
    this.getFiltersData();
    this.usersData = [];
    this.loadUsers();
  }

  ngAfterViewInit() {
    // attaching event on search icon explictly
    this.searchIconEl = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (this.searchIconEl) {
      this.boundOnFilter = this.onFilter.bind(this);
      this.searchIconEl.addEventListener("click", this.boundOnFilter);
    }
  }

  showMoreLocations(user: User) {
    user.showMore = !user.showMore;
  }

  getFiltersData() {
    let observables = [];
    this.utilityService.increaseSpinnerCount();
    observables.push(this.resourceManagementService.getJobRoleDetails());
    observables.push(
      this.companyManagementService.listCompanyLocations(this.company.companyId)
    );
    forkJoin(observables).subscribe(
      (outputs) => {
        if (outputs[0]) {
          this.departmentList = outputs[0]["Department"].map((item) => {
            return {
              name: item.description,
            };
          });

          this.disciplineList = outputs[0]["DisciplineName"]
            .filter((re) => re.description !== "No Discipline")
            .map((item) => {
              return {
                name: item.description,
              };
            });
        }

        if (outputs[1]) {
          let pagedCompanyLocation = outputs[1] as PagedCompanyLocation;
          this.locationList = pagedCompanyLocation.locations as CompanyLookup[];
        }
        this.utilityService.decreaseSpinnerCount();
      },
      (err) => {
        console.error(`Error loading up company lookup data. ${err}`);
        this.utilityService.decreaseSpinnerCount();
      }
    );
  }

  onFilter() {
    this.usersData = [];
    this.currentPage = 0;
    this.loadUsers();
  }

  loadUsers() {
    this.loadingUsers = true;

    this.loadFilters();
    this.usersSubscription = this.userProfileService
      .getUsersByRole(this.filterParameters)
      .subscribe(
        (response) => {
          if (response && response["hits"] && response["totalHits"] > 0) {
            this.usersData.push(...this.roleService.mapUsers(response["hits"]));
            this.totalPageCount = Math.ceil(
              response["totalHits"] / this.pageSize
            );
            this.lastFrom = this.currentPage * this.pageSize;
            this.lastRecords.currentFrom = this.filterParameters.from;
            this.lastRecords.totalHits = response["totalHits"];
            this.currentPage++;
            this.allUsersSelected =
              this.usersData.length === this.usersSelected;
          } else if (!response["hits"] || response["totalHits"] == 0) {
            this.lastRecords.currentFrom = this.filterParameters.from;
            this.lastRecords.totalHits = 0;
          }
          this.loadingUsers = false;
          this.totalPageCount = Math.ceil(
            this.usersData.length / this.pageSize
          );
        },
        (err) => {
          this.loadingUsers = false;
          this.utilityService.presentErrorToast(err, "Error loading Users.");
        }
      );
  }

  loadFilters() {
    this.filterParameters = {};
    this.filterParameters.entityType = "USER";
    this.filterParameters.from = this.currentPage * this.pageSize;
    this.filterParameters.size = this.pageSize;
    this.filterParameters.mustFields = [
      {
        name: "companyId",
        value: this.company.companyId,
      },
    ];

    if (this.isEdit) {
      // users in this role to view/remove
      this.filterParameters.nestedFields = [
        {
          path: "orgRoles.roles",
          name: "orgRoles.roles.id",
          value: this.role.id,
          type: "mustField",
        },
      ];
    } else {
      // users not in this role
      this.filterParameters.nestedFields = [
        {
          path: "orgRoles.roles",
          name: "orgRoles.roles.id",
          value: this.role.id,
          type: "mustNotField",
        },
      ];
    }

    if (this.locationSelected) {
      this.filterParameters.nestedFields.push({
        path: "orgRoles",
        name: "orgRoles.abbreviation",
        value: this.locationSelected,
        type: "mustField",
      });
    }

    if (this.departmentSelected) {
      this.filterParameters.mustFields.push({
        name: "department.keyword",
        value: this.departmentSelected,
      });
    }

    if (this.disciplineSelected) {
      this.filterParameters.mustFields.push({
        name: "disciplines.keyword",
        value: getDisciplineCodeByDescription(this.disciplineSelected),
      });
    }

    if (this.query) {
      this.filterParameters.search = this.query;
    }
    this.filterParameters.sortBy = {
      name: this.sortOptions.column,
      value: this.sortOptions.direction,
    };
  }

  goBack() {
    this.hideComponent.emit(true);
  }

  onSearchCancel() {
    this.query = null;
    this.usersData = [];
    this.currentPage = 0;
    if (this.filterParameters && this.filterParameters.search) {
      this.filterParameters.search = null;
    }
    this.loadUsers();
  }

  /** aria-sort for a user-grid column header (direction was icon-only before). */
  ariaSort(column: string): string {
    if (this.sortOptions.column !== column) {
      return "none";
    }
    return this.sortOptions.direction === "asc" ? "ascending" : "descending";
  }

  sortBy(selectedCol) {
    this.usersData = [];
    this.currentPage = 0;
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }

    this.loadUsers();
  }

  selectAllUsers() {
    this.allUsersSelected = !this.allUsersSelected;
    this.usersData = this.usersData.map((user) => ({
      ...user,
      selected: this.allUsersSelected,
    }));
  }

  selectUser(user) {
    user.selected = !user.selected;
    this.allUsersSelected = this.usersData.length === this.usersSelected;
  }

  get usersSelected(): number {
    return this.usersData.filter((u) => u.selected).length;
  }

  addUsers() {
    let confirmModal = this.modalController.create(
      "ConfirmAddUserToRoleModal",
      {
        title: "Add selected user(s)",
        message: `Please confirm you are adding (${this.usersSelected}) Users to the ${this.role.name} Permission Group.`,
      },
      { enableBackdropDismiss: true, cssClass: "confirmAddUserToRole" }
    );

    confirmModal.onDidDismiss((data) => {
      if (data && data.dismiss) {
        this.processRoleRequest("adding");
      }
    });

    confirmModal.present();
  }

  removeUsers() {
    let confirmModal = this.modalController.create(
      "ConfirmAddUserToRoleModal",
      {
        title: "Remove selected user(s)",
        message: `Please confirm you are removing (${this.usersSelected}) Users from the ${this.role.name} Permission Group.`,
      },
      { enableBackdropDismiss: true, cssClass: "confirmAddUserToRole" }
    );

    confirmModal.onDidDismiss((data) => {
      if (data && data.dismiss) {
        this.processRoleRequest("removing");
      }
    });

    confirmModal.present();
  }

  processRoleRequest(action: string) {
    this.utilityService.increaseSpinnerCount(`${action} users...`);
    let request = this.prepareRoleRequest();

    forkJoin(
      this.userProfileService.postRolePermission(request),
      this.resourceManagementService.updateRoles(request)
    ).subscribe(
      () => {
        this.utilityService.decreaseSpinnerCount();
        this.usersData = this.usersData.filter((user) => user.selected != true);
        this.utilityService.presentSuccessToast(
          `User(s) ${action === "adding" ? "added" : "removed"} successfully`
        );
      },
      (err) => {
        this.utilityService.decreaseSpinnerCount();
        let msg = `Error ${action} users.`;
        this.utilityService.presentErrorToast(err, msg);
      }
    );
  }

  prepareRoleRequest(): RoleUpdate {
    let request: RoleUpdate = {} as RoleUpdate;
    request.addToRole = !this.isEdit;
    request.userIds = [] as number[];
    request.roleId = this.role.id;
    request.roleName = this.role.name;
    request.resourcesIds = [];

    this.usersData
      .filter((u) => u.selected)
      .forEach((u) => {
        request.userIds.push(+u.userId);

        u.tenantId.forEach((t) => {
          request.resourcesIds.push({ externalId: +u.userId, tenantId: +t });
        });
      });

    return request;
  }

  clearFilters() {
    this.departmentSelected = null;
    this.disciplineSelected = null;
    this.locationSelected = null;
    this.query = null;
    this.filterParameters = {};
    this.usersData = [];
    this.currentPage = 0;
    this.loadUsers();
  }

  ngOnDestroy(): void {
    if (this.usersSubscription) {
      this.usersSubscription.unsubscribe();
    }
    if (this.searchIconEl && this.boundOnFilter) {
      this.searchIconEl.removeEventListener("click", this.boundOnFilter);
    }
  }

  doInfinite(infiniteScroll) {
    this.loadUsers();
    setTimeout(() => {
      infiniteScroll.complete();
      infiniteScroll.enable(this.usersData.length < this.lastRecords.totalHits);
    }, 500);
  }

  checkForScrollbar() {
    if (this.contentPlaceholder) {
      const scrollElement = this.contentPlaceholder.getScrollElement();
      this.hasScrollbar =
        scrollElement.scrollHeight > scrollElement.clientHeight;
      if (
        !this.hasScrollbar &&
        this.usersData.length < this.lastRecords.totalHits
      ) {
        this.loadUsers();
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/add-user-to-role/add-user-to-role.ts